import { Input, type InputProps, Label } from '@nrg/design-system';
import { type ReactNode, useId } from 'react';

export interface FormFieldProps extends Omit<InputProps, 'id' | 'invalid'> {
  label: ReactNode;
  error?: string | null | undefined;
  hint?: ReactNode;
}

export default function FormField({ label, error, hint, ...inputProps }: FormFieldProps) {
  const reactId = useId();
  const id = inputProps.name ? `field-${inputProps.name}` : reactId;
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--nrg-space-1-5)',
      }}
    >
      <Label as="label" htmlFor={id} size="sm" tone="strong" spacing="wider">
        {label}
      </Label>
      <Input
        id={id}
        invalid={Boolean(error)}
        aria-invalid={error ? true : undefined}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        {...inputProps}
      />
      {hint ? (
        <span
          id={hintId}
          style={{
            fontSize: 'var(--nrg-font-size-body-sm)',
            color: 'var(--nrg-color-text-muted)',
            lineHeight: 'var(--nrg-line-height-body-sm)',
          }}
        >
          {hint}
        </span>
      ) : null}
      {error ? (
        <span
          id={errorId}
          role="alert"
          style={{
            fontSize: 'var(--nrg-font-size-body-sm)',
            color: 'var(--nrg-color-critical)',
            lineHeight: 'var(--nrg-line-height-body-sm)',
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}
